import { useMemo } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData
} from "@tanstack/react-query";
import { QUERY_GET_PROPERTIES_COMPACT_WITH_PHONE } from "api/landlord/property-groups";
import { useAuthContext } from "providers/Auth";
import { phoneNumbersApi } from "./api";
import { phoneNumberKeys } from "./queryKeys";
import {
  fetchLeasingAgentConfigs,
  LEASING_AGENT_CONFIGS_KEY
} from "views/settings-users-add-agent/lib/api";
import type {
  ApplyCallHandlingPayload,
  AssignedPropertiesPayload,
  BuyPhoneNumberPayload,
  ImportFromTwilioPayload,
  SearchAvailableParams,
  UpdatePhoneNumberPayload
} from "./types";

export const useAllPhoneNumbers = ({
  enabled = true,
  propertyIds,
  withInboundRecipients = false,
  search
}: {
  enabled?: boolean;
  propertyIds?: number[];
  withInboundRecipients?: boolean;
  search?: string;
} = {}) =>
  useQuery({
    queryKey: [
      ...phoneNumberKeys.lists(),
      "ALL",
      propertyIds ?? [],
      withInboundRecipients,
      search ?? ""
    ],
    queryFn: () =>
      phoneNumbersApi.listAll({ propertyIds, withInboundRecipients, search }),
    enabled,
    placeholderData: keepPreviousData
  });

export const useSearchAvailableNumbers = (
  params: SearchAvailableParams,
  enabled: boolean
) =>
  useQuery({
    queryKey: phoneNumberKeys.searchAvailable(params),
    queryFn: () => phoneNumbersApi.searchAvailable(params),
    enabled,
    placeholderData: keepPreviousData
  });

export const useBuyPhoneNumber = () => {
  const queryClient = useQueryClient();
  const { refreshPartnerData } = useAuthContext();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "CREATE"],
    mutationFn: (payload: BuyPhoneNumberPayload) =>
      phoneNumbersApi.buy(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: [QUERY_GET_PROPERTIES_COMPACT_WITH_PHONE]
      });
      // The max_provisioned_phones lock reads the partner entity's unfiltered
      // count, so it has to be re-fetched whenever the count changes (CRM-759).
      refreshPartnerData();
    }
  });
};

export const useImportFromTwilio = () => {
  const queryClient = useQueryClient();
  const { refreshPartnerData } = useAuthContext();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "CREATE"],
    mutationFn: (payload: ImportFromTwilioPayload) =>
      phoneNumbersApi.importFromTwilio(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() });
      refreshPartnerData();
    }
  });
};

export const useUpdatePhoneNumber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "UPDATE"],
    mutationFn: ({
      commId,
      payload
    }: {
      commId: string;
      payload: UpdatePhoneNumberPayload;
    }) => phoneNumbersApi.update(commId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() });
    }
  });
};

export const useLeasingAgentConfigs = (enabled: boolean) =>
  useQuery({
    queryKey: LEASING_AGENT_CONFIGS_KEY,
    queryFn: fetchLeasingAgentConfigs,
    enabled,
    retry: false
  });

export const useDeletePhoneNumber = () => {
  const queryClient = useQueryClient();
  const { refreshPartnerData } = useAuthContext();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "DELETE"],
    mutationFn: ({
      commId,
      newDefaultPhoneNumberId
    }: {
      commId: string;
      newDefaultPhoneNumberId?: string;
    }) => phoneNumbersApi.remove(commId, newDefaultPhoneNumberId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_GET_PROPERTIES_COMPACT_WITH_PHONE]
        })
      ]);
      // Freeing a number has to unlock the buy/import affordance right away.
      refreshPartnerData();
    }
  });
};

export const usePropertyPhoneDefaults = (enabled: boolean = true) =>
  useQuery({
    queryKey: phoneNumberKeys.propertyDefaults(),
    queryFn: () => phoneNumbersApi.listPropertyDefaults(),
    enabled
  });

export const useAiAgentDigits = () => {
  const { data } = useQuery({
    queryKey: phoneNumberKeys.aiAgentNumbers(),
    queryFn: phoneNumbersApi.listAiAgentNumbers
  });

  return useMemo(
    () =>
      new Set(
        (data ?? []).map((number) => number.replace(/\D/g, "").slice(-10))
      ),
    [data]
  );
};

export const useRecipientsFor = (ids: number[]) =>
  useQuery({
    queryKey: phoneNumberKeys.withRecipients(ids),
    queryFn: () => phoneNumbersApi.listWithRecipients(ids),
    enabled: ids.length > 0,
    placeholderData: keepPreviousData
  });

export const useRingingUsers = (phoneNumberId: number, enabled: boolean) =>
  useQuery({
    queryKey: phoneNumberKeys.ringingUsers(phoneNumberId),
    queryFn: () => phoneNumbersApi.listRingingUsers(phoneNumberId),
    enabled
  });

export const usePartnerUsers = (enabled: boolean = true) =>
  useQuery({
    queryKey: ["PHONE_NUMBERS", "PARTNER_USERS"],
    queryFn: phoneNumbersApi.listUsers,
    enabled
  });

export const usePropertyPhoneDefault = (
  propertyId: number,
  enabled: boolean = true
) =>
  useQuery({
    queryKey: phoneNumberKeys.propertyDefault(propertyId),
    queryFn: () => phoneNumbersApi.getPropertyDefault(propertyId),
    enabled
  });

export const useSetPropertyPhoneDefault = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "PROPERTY_DEFAULT", "UPDATE"],
    mutationFn: ({
      propertyId,
      phoneNumberId
    }: {
      propertyId: number;
      phoneNumberId: string;
    }) => phoneNumbersApi.setPropertyDefault(propertyId, phoneNumberId),
    onSuccess: (_data, { propertyId }) => {
      queryClient.invalidateQueries({
        queryKey: phoneNumberKeys.propertyDefaults()
      });
      queryClient.invalidateQueries({
        queryKey: phoneNumberKeys.propertyDefault(propertyId)
      });
      queryClient.invalidateQueries({
        queryKey: phoneNumberKeys.lists(),
        refetchType: "none"
      });
    }
  });
};

export const useApplyCallHandling = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "CALL_HANDLING", "UPDATE"],
    mutationFn: ({
      commId,
      payload
    }: {
      commId: string;
      payload: ApplyCallHandlingPayload;
    }) => phoneNumbersApi.applyCallHandling(commId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() });
      queryClient.removeQueries({ queryKey: LEASING_AGENT_CONFIGS_KEY });
    }
  });
};

export const useUpdateAssignedProperties = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["PHONE_NUMBERS", "UPDATE_PROPERTIES"],
    mutationFn: ({
      commId,
      payload
    }: {
      commId: string;
      payload: AssignedPropertiesPayload;
    }) => phoneNumbersApi.updateAssignedProperties(commId, payload),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: phoneNumberKeys.lists() }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_GET_PROPERTIES_COMPACT_WITH_PHONE]
        })
      ]);
    }
  });
};
